import {filterMap} from '@benchling/js-utils';
import classNames from 'classnames';
import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {getArchiveItemName} from 'archive/ArchivePurposesUtil';
import BenchlingConfig, {SnapshotChipsAllowList} from 'BenchlingConfig';
import {ArchiveDataNode, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExportChipState} from 'notebook/entries/ExportChipState';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {BenchlingState} from 'redux-stores/reduceApp';
import {isFailed} from 'schemas/SchemaValidationChecks';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import NoAccessChip from 'ui-library/chips/NoAccessChip';
import useHoverState from 'ui-library/hooks/useHoverState';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';

import {isInAllowList} from './SnapshotChipTypes';

export interface SnapshotChipProps {
  // This is the chip that we should render if we don't have any Snapshot Chip information
  children: React.ReactNode;

  // This is the ID of the Benchling entity that we're trying to show a chip of
  id?: string | null;

  // The size of the chip that we want to show
  size: ChipSize;

  // The text that we want to use instead of the Chip name if we've frozen the chip
  displayNameOverride?: string | null;

  // CSS Styling used to match the originally wrapped Chip
  className?: string | null;

  // Controls whether or not the secondary text will be used in the Chip
  useSecondaryText?: boolean;

  // Matches the negative prop of <Chip/>
  negative?: boolean | null;

  // This is used in conjunction with the ELN_EXPORT_SNAPSHOT_CHIPS_ALLOW_LIST config flag to determine if a
  // chip should be rendered as a SnapshotChip. If false, snapshot chip is bypassed and the children are
  // rendered instead, as a regular SmartChip.
  chipType: keyof SnapshotChipsAllowList;
}

type InnerChipProps = Omit<SnapshotChipProps, 'documentId'> &
  ExportChipState & {id?: string | null; isHovered: boolean; secondaryText?: string | null};

function getDisplayText({
  id,
  text,
  displayNameOverride,
  isArchived,
  archivePurpose,
}: {
  id?: string | null;
  text: string;
  displayNameOverride?: string | null;
  isArchived?: boolean | null;
  archivePurpose?: string | null;
}): string {
  const displayText = displayNameOverride ?? text;

  if (id && isArchived) {
    const archiveData: ArchiveDataNode = {
      __typename: 'ArchiveDataNode',
      archived: Boolean(isArchived),
      purpose: archivePurpose ?? null,
    };
    const snapshotItem = {
      id,
      name: displayText,
      archiveData,
    };
    return getArchiveItemName(snapshotItem);
  }

  return displayText;
}

function InnerChip({
  children,
  href,
  icon,
  isHovered,
  size,
  text,
  isArchived,
  secondaryText,
  previewFileUrl,
  isPDFPreviewFileMissing,
  isInaccessible,
  className,
  useSecondaryText = true,
  negative,
  chipType,
}: InnerChipProps): JSX.Element {
  const hasPopoverContent = [
    'BioentityChip',
    'AnalysisChip',
    'ProcessTaskChip',
    // StorableChip is not in the allow list, but has popoverContentOverride
  ].includes(chipType);
  if (isHovered) {
    return <>{children}</>;
  } else if (previewFileUrl) {
    return <img src={previewFileUrl} alt="blob preview" className="blobLinkPreview-image" />;
  } else {
    // isPDFPreviewFileMissing is used to track if we want to render the chip in the warning style
    // in the same way we would given that the below flags are enabled
    const {attachResultBlobsInline, includeFileAttachmentsInline} = BenchlingConfig.ELN_EXPORT_PDF_CONFIG;
    return isInaccessible ? (
      <NoAccessChip className={className ?? ''} size={size} />
    ) : (
      <Chip
        text={text}
        className={classNames(
          className,
          attachResultBlobsInline &&
            includeFileAttachmentsInline &&
            isPDFPreviewFileMissing &&
            'chip--warningLighter'
        )}
        faded={isArchived}
        href={href}
        icon={icon}
        size={size}
        secondaryText={useSecondaryText ? secondaryText : null}
        negative={negative}
        // This immitates the behavior of the SmartChip component. The chips in the hasPopoverContent list are
        // the ones that have a popoverContentOverride prop. When this exists, SmartChip will wrap the Chip
        // with a Popover component which will sublty change the appearance of the Chip. For SnapshotChips
        // we are not concerned about the contents of the popover so we just pass an empty span.
        popoverContent={hasPopoverContent ? <span /> : null}
      />
    );
  }
}

interface SnapshotChipInPrintPreviewProps extends SnapshotChipProps {
  documentId?: string;
}

function SnapshotChipInPrintPreview({
  id,
  children,
  displayNameOverride,
  documentId,
  size,
  className,
  useSecondaryText,
  chipType,
}: SnapshotChipInPrintPreviewProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  const chipInfo = useSelector(
    (state: BenchlingState) => documentId && id && state.ui.export[documentId]?.chips[id]
  );
  const [hasBeenHovered, setHasBeenHovered] = useState(false);

  if (isHovered && !hasBeenHovered) {
    setHasBeenHovered(true);
  }

  if (!chipInfo || hasBeenHovered) {
    return <>{children}</>;
  }

  const isBioentity = chipType === 'BioentityChip';
  let negative: boolean | null = false;
  let bioentityIconOverride;
  // We want to match the logic of BioentityChip, which is the only chip that currently checks
  // schemaValidationStatus and has a custom icon logic.
  if (id && isBioentity) {
    negative = chipInfo?.schemaValidationStatus ? isFailed({status: chipInfo.schemaValidationStatus}) : null;
    bioentityIconOverride = _getIconForBioentityChip(id, chipInfo.tagSchemaIconId, chipInfo.sequenceType);
  }
  return (
    <span {...hoverProps}>
      <InnerChip
        {...chipInfo}
        text={getDisplayText({...chipInfo, id, displayNameOverride})}
        size={size}
        isHovered={isHovered}
        className={className}
        useSecondaryText={useSecondaryText}
        {...(isBioentity ? {icon: bioentityIconOverride, negative} : {})}
        chipType={chipType}
      >
        {children}
      </InnerChip>
    </span>
  );
}

// TODO(BNCH-102958): This needs to be moved into and exported by client/ui-library/chips/getIconForBioentity.ts
// We should then update getIconForBioentity in the ui-library to use the same function to avoid duplication.
function _getIconForBioentityChip(
  id: string,
  tagSchemaIconId?: BenchlingIconName | null,
  sequenceType?: SequenceType | null
): BenchlingIconName | null {
  if (tagSchemaIconId) {
    return tagSchemaIconId;
  } else if (sequenceType) {
    if (sequenceType === SequenceType.ds) {
      return FolderItemIcon.Sequence;
    } else if (sequenceType === SequenceType.ss) {
      return FolderItemIcon.RNASequence;
    } else {
      return FolderItemIcon.OligoV2;
    }
  } else {
    return apiIdToIconName(id);
  }
}

/**
 * A SnapshotChip will only show up when in print preview mode. If it's not in print preview mode, then it
 * will just render the children given to it. If it is in print preview mode, then the SnapshotChip will load
 * its information from the Redux Store, if it's available. If there isn't any information for the chip in the
 * Redux store, then it will show the children provided instead.
 *
 * The Chip will also show the children if the user hovers over the chip. Once we've shown the children, we'll
 * always show the children. This allows us to progressively enhance the interactivity. However, we're
 * currently gating this all to the Print Preview view and, eventually, the Export view.
 */
export function SnapshotChip(props: SnapshotChipProps): JSX.Element {
  const {isPrintPreviewView, fileId} = EditorPropertiesContext.useContext();

  const isAllowedChipType = isInAllowList(props.chipType);
  const shouldRenderSnapshotChip = isPrintPreviewView && isAllowedChipType;

  return shouldRenderSnapshotChip ? (
    <SnapshotChipInPrintPreview {...props} documentId={fileId} />
  ) : (
    <>{props.children}</>
  );
}

interface SnapshotChipListProps {
  children: React.ReactNode;
  documentId?: string;
  size: ChipSize;
  ids: Array<string | null>;
  maxChips?: number;
  className?: string;
  useSecondaryText?: boolean;
}

// Stable fallback so the selector below doesn't return a new object reference on every call.
const EMPTY_CHIPS: Record<string, ExportChipState> = {};

function SnapshotChipListInPrintPreview({
  children,
  documentId,
  size,
  ids,
  maxChips = Number.MAX_SAFE_INTEGER,
  className,
  useSecondaryText,
}: SnapshotChipListProps): JSX.Element {
  const chips = useSelector((state: BenchlingState) =>
    documentId ? (state.ui.export[documentId]?.chips ?? EMPTY_CHIPS) : EMPTY_CHIPS
  );
  // filterMap always builds a new object, so memoize it outside of the selector.
  const chipInfos = useMemo(
    () => (documentId ? filterMap(chips, (_, id) => ids.includes(id)) : undefined),
    [documentId, chips, ids]
  );

  if (!chipInfos || Object.entries(chipInfos).length === 0) {
    return <>{children}</>;
  }

  const numOverflowChips = ids.length - maxChips;
  const displayedChips = ids.slice(0, maxChips);

  return (
    <div>
      {displayedChips.map((id) => {
        if (id && chipInfos.hasOwnProperty(id)) {
          let negative: boolean | null = false;
          const bioentity = chipInfos[id];
          if (id && APIUtil.isBiologicalEntity(id)) {
            negative = bioentity?.schemaValidationStatus
              ? isFailed({status: bioentity.schemaValidationStatus})
              : null;
          }

          return (
            // added div and blobLinkPreview to replicate BlobLink:
            // https://github.com/benchling/aurelia/blob/f128104e87fec87ac4214400dd931d12ec430015/client/notebook/data-tables/BlobLinkList.tsx#L207
            // https://github.com/benchling/aurelia/blob/f128104e87fec87ac4214400dd931d12ec430015/client/notebook/data-tables/BlobLinkList.tsx#L165
            <div className="blobLinkPreview" key={id}>
              <InnerChip
                {...chipInfos[id]}
                icon={null}
                text={getDisplayText({...chipInfos[id], id})}
                size={size}
                isHovered={false}
                className={className}
                useSecondaryText={useSecondaryText}
                negative={negative}
                chipType="BlobLinkList"
              >
                <></>
              </InnerChip>
            </div>
          );
        }
        return null;
      })}
      {numOverflowChips > 0 ? (
        <Chip href="#" size={ChipSize.MINI} text={`+${numOverflowChips} more`} key="overflow_chip" />
      ) : null}
    </div>
  );
}

export function SnapshotChipList(props: SnapshotChipListProps): JSX.Element {
  const {isPrintPreviewView} = EditorPropertiesContext.useContext();

  // TODO(BNCH-102958): chipType is temporarily hardcoded because SnapshotChipClist is only used to wrap
  // BlobLinkList. The intention is to refactor BlobLinkList to use SnapshotChip for each listed BlobLink
  // chip. This will completely remove the need for SnapshotChipList.
  const isAllowedChipType = isInAllowList('BlobLinkList');
  const shouldRenderSnapshotChip = isPrintPreviewView && isAllowedChipType;

  return shouldRenderSnapshotChip ? <SnapshotChipListInPrintPreview {...props} /> : <>{props.children}</>;
}
